/**
 * Central registry of lazy-route importers. Same import() functions used by
 * React.lazy in App.tsx — React caches module promises, so calling them here
 * warms the chunk without re-downloading it.
 */
const routeImporters: Record<string, () => Promise<unknown>> = {
  "/": () => import("@/pages/Landing"),
  "/dashboard": () => import("@/pages/Index"),
  "/login": () => import("@/pages/Login"),
  "/signup": () => import("@/pages/SignUp"),
  "/reset-password": () => import("@/pages/ResetPassword"),
  "/admin": () => import("@/pages/AdminLogin"),
  "/services": () => import("@/pages/Services"),
  "/billing": () => import("@/pages/Billing"),
  "/templates": () => import("@/pages/Templates"),
  "/resume-examples": () => import("@/pages/ResumeExamples"),
  "/cover-letter-templates": () => import("@/pages/CoverLetterTemplates"),
  "/career-blog": () => import("@/pages/CareerBlog"),
  "/privacy": () => import("@/pages/Privacy"),
  "/terms": () => import("@/pages/Terms"),
  "/ai-resume-builder": () => import("@/pages/AIResumeBuilder"),
  "/ats-resume-checker": () => import("@/pages/ATSResumeChecker"),
  "/resume-builder-india": () => import("@/pages/ResumeBuilderIndia"),
  "/resume-builder-for-engineers": () => import("@/pages/ResumeBuilderEngineers"),
  "/resume-builder-for-freshers": () => import("@/pages/ResumeBuilderFreshers"),
  "/free-resume-maker": () => import("@/pages/FreeResumeMaker"),
  "/resume-format-pdf": () => import("@/pages/ResumeFormatPDF"),
  "/recruiter/signup": () => import("@/pages/RecruiterSignUp"),
  "/recruiter/login": () => import("@/pages/RecruiterLogin"),
  "/champions": () => import("@/pages/Champions"),
  "/oracle-support": () => import("@/pages/OracleSupport"),
  "/layoff-support": () => import("@/pages/LayoffSupport"),
  "/gcc-resume": () => import("@/pages/GCCResume"),
  "/mba-resume": () => import("@/pages/MBAResume"),
  "/global-resume": () => import("@/pages/GlobalResume"),
};

const warmed = new Set<string>();

export function prefetchRoute(path: string): void {
  // Strip query/hash and normalize
  const clean = path.split("?")[0].split("#")[0];
  if (warmed.has(clean)) return;
  const importer = routeImporters[clean];
  if (!importer) return;
  warmed.add(clean);
  // Fire and forget — errors are ignored (chunk will retry on actual nav)
  importer().catch(() => warmed.delete(clean));
}

/** Warm the highest-traffic marketing routes during idle time. */
export function prefetchHotRoutes(): void {
  const hot = [
    "/services",
    "/login",
    "/signup",
    "/ai-resume-builder",
    "/ats-resume-checker",
    "/templates",
    "/free-resume-maker",
  ];
  const run = () => hot.forEach(prefetchRoute);
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
    .requestIdleCallback;
  if (typeof ric === "function") {
    ric(run, { timeout: 3000 });
  } else {
    setTimeout(run, 1500);
  }
}
